import * as React from 'react'
import { observer } from 'mobx-react'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { useDocumentTitle } from 'hooks/document-title'
import { ContentLayout, ContentMain } from 'components/ContentLayout'
import InventoryManagerHeader from '../../components/InventoryManagerHeader'
import AdminInventoryManagerViewModel from '../../InventoryManagerViewModel'
import InventoryTable from 'admin/inventory/components/InventoryTable'
import InventoryViewerHeader from 'admin/inventory/components/InventoryViewerHeader'
import CreateInventoryItemDialog from '../CreateInventoryItemDialog'
import EditInventoryItemDialog from '../EditInventoryItemDialog'
import ArchiveItemDialog from '../ArchiveItemModal'
import UnarchiveItemDialog from '../UnarchiveItemModal'
import CreateWorkspaceInventoryItemDialog from '../CreateWorkspaceInventoryItemDialog'
import OverrideInventoryItemDialog from '../OverrideInventoryItemDialog'
import InventoryViewer from '../../components/InventoryViewer'
import { InventoryItemTypeFilter } from 'admin/inventory/InventorySearchViewModel'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import { useActivation } from 'hooks/mobx-hooks'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import styles from './InventoryManager.sass'

interface InventoryManagerProps {
  vm: AdminInventoryManagerViewModel
}

export default observer(function InventoryManager({
  vm,
}: InventoryManagerProps) {
  useDocumentTitle('Service Manager')
  useActivation(vm)

  return vm.activate.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => <InventoryManagerContent vm={vm} />,
  })
})

const InventoryManagerContent = observer(function InventoryManagerContent({
  vm,
}: InventoryManagerProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  const inventoryTypeFilter = vm.searcher.filters.getFilter(
    'inventoryItemType',
  ) as SingleSelectFilter<string, InventoryItemTypeFilter>

  return (
    <div className={styles.root} ref={scrollRef}>
      <InventoryManagerHeader scrollOffset={scrollOffset} vm={vm} />
      <ContentLayout accountForLabel ref={scrollRef}>
        <ContentMain constrainWidth>
          <InventoryViewer filter={inventoryTypeFilter} />
          <div className={styles.inventoryViewer}>
            <InventoryViewerHeader vm={vm} />
            <div className={styles.inventoryTableContainer}>
              <InventoryTable vm={vm}></InventoryTable>
            </div>
          </div>
        </ContentMain>
      </ContentLayout>
      <CreateInventoryItemDialog vm={vm.createInventoryItemViewModel} />
      <CreateWorkspaceInventoryItemDialog
        vm={vm.createWorkspaceInventoryItemViewModel}
      />
      <EditInventoryItemDialog vm={vm.editInventoryItemViewModel} />
      <OverrideInventoryItemDialog vm={vm.overrideInventoryItemViewModel} />
      <ArchiveItemDialog vm={vm.archiveItemViewModel} />
      <UnarchiveItemDialog vm={vm.unarchiveItemViewModel} />
    </div>
  )
})
